import { useEffect, useState } from 'react';

function cssEscape(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
  return String(value).replace(/"/g, '\\"');
}

/**
 * Id of the outline heading at the top of the scrolling document.
 * Headings are resolved inside rootSelector; the scroll parent is the
 * nearest .rte-document-body (the editor and the read page both use it).
 */
export default function useActiveOutlineHeading(headingIds, rootSelector) {
  const idsKey = (headingIds || []).join('\0');
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const ids = idsKey ? idsKey.split('\0') : [];
    if (!ids.length || !rootSelector) {
      setActiveId(null);
      return undefined;
    }

    let cancelled = false;
    let frame = 0;
    let scroller = null;
    let observer = null;

    const update = () => {
      if (cancelled) return;
      const root = document.querySelector(rootSelector);
      if (!root) return;
      const nodes = ids
        .map((id) => root.querySelector(`[id="${cssEscape(id)}"]`))
        .filter(Boolean);
      if (!nodes.length) return;

      const scrollParent = root.closest('.rte-document-body') || root;
      const edge = scrollParent.getBoundingClientRect().top + 80;
      const maxScroll = scrollParent.scrollHeight - scrollParent.clientHeight;
      const atBottom = maxScroll > 4 && maxScroll - scrollParent.scrollTop < 4;
      let current = nodes[0];
      if (atBottom && nodes.length > 1) {
        current = nodes[nodes.length - 1];
      } else {
        for (const node of nodes) {
          if (node.getBoundingClientRect().top <= edge) current = node;
          else break;
        }
      }
      const nextId = current.id;
      setActiveId((prev) => (prev === nextId ? prev : nextId));
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    const attach = () => {
      if (cancelled) return;
      const root = document.querySelector(rootSelector);
      if (!root) {
        frame = requestAnimationFrame(attach);
        return;
      }
      scroller = root.closest('.guides-read-scroll, .rte-document-body') || root;
      scroller.addEventListener('scroll', schedule, { passive: true });
      document.addEventListener('scroll', schedule, { passive: true, capture: true });
      window.addEventListener('resize', schedule);
      observer = new MutationObserver(schedule);
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id']
      });
      schedule();
    };

    attach();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      scroller?.removeEventListener('scroll', schedule);
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [idsKey, rootSelector]);

  return activeId;
}
